import { getApiUrl } from '@/shared/api/get-api-url';
import { redirectToSignIn } from '@/shared/api/redirect-to-sign-in';

export class ApiError extends Error {
    constructor(
        public status: number,
        message: string,
        public response?: unknown,
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

type RequestConfig = {
    method?: string;
    headers?: HeadersInit;
    body?: unknown;
    signal?: AbortSignal;
};

class ApiClient {
    constructor() {}

    private async request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
        const { method = 'GET', headers = {}, body, signal } = config;

        const url = getApiUrl(endpoint);

        const response = await fetch(url, {
            method,
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...headers,
            },
            body: body ? JSON.stringify(body) : undefined,
            signal,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));

            if (response.status === 401) {
                redirectToSignIn(true);
                return new Promise<T>(() => {});
            }

            throw new ApiError(
                response.status,
                errorData.message || `HTTP ${response.status}: ${response.statusText}`,
                errorData,
            );
        }

        if (response.status === 204) {
            return undefined as T;
        }

        return response.json();
    }

    async get<T>(endpoint: string, signal?: AbortSignal): Promise<T> {
        return this.request<T>(endpoint, { method: 'GET', signal });
    }

    async post<T>(endpoint: string, data?: unknown, signal?: AbortSignal): Promise<T> {
        return this.request<T>(endpoint, { method: 'POST', body: data, signal });
    }

    async put<T>(endpoint: string, data: unknown, signal?: AbortSignal): Promise<T> {
        return this.request<T>(endpoint, { method: 'PUT', body: data, signal });
    }

    async patch<T>(endpoint: string, data: unknown, signal?: AbortSignal): Promise<T> {
        return this.request<T>(endpoint, { method: 'PATCH', body: data, signal });
    }

    async delete<T>(endpoint: string, signal?: AbortSignal): Promise<T> {
        return this.request<T>(endpoint, { method: 'DELETE', signal });
    }
}

export const apiClient = new ApiClient();
